import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

/**
 * The first-run offer, shown once per user.
 *
 * The tour is opt-in rather than automatic: a new account lands in the
 * studio while its sample reading is still being created, so anything
 * that starts by itself arrives seconds late and on top of content that
 * appeared without warning. Asking first turns that dead time into
 * something explained, and everything after it is user-initiated.
 */
interface Props {
  open: boolean;
  /** Sample content is still being prepared, so the tour has nothing
   * to point at yet. */
  preparing: boolean;
  onTakeTour: () => void;
  onSkip: () => void;
}

export default function WelcomeDialog({
  open,
  preparing,
  onTakeTour,
  onSkip,
}: Props) {
  return (
    <Dialog
      open={open}
      onClose={onSkip}
      maxWidth="xs"
      fullWidth
      aria-labelledby="welcome-dialog-title"
    >
      <DialogTitle id="welcome-dialog-title">Welcome to AnnoGram</DialogTitle>
      <DialogContent>
        {/* DialogContentText is muted by default; this copy is the whole
            point of the dialog, so it reads at title strength. */}
        <DialogContentText color="text.primary">
          AnnoGram takes you from reading a paper to diagramming its
          argument, testing whether that argument holds, and writing it up.
        </DialogContentText>
        <DialogContentText color="text.primary" sx={{ mt: 2 }}>
          {preparing
            ? "We're setting up a sample reading you can practise on — one moment."
            : "There's a sample reading in your workspace to practise on. A two-minute tour will show you around it, or you can explore on your own."}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onSkip} color="inherit">
          Skip for now
        </Button>
        <Button
          onClick={onTakeTour}
          variant="contained"
          color="secondary"
          disabled={preparing}
          startIcon={
            preparing ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {preparing ? "Preparing…" : "Take the tour"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
